/**
 * AdminSystemHealthTab Component
 *
 * System health monitoring dashboard for the admin panel.
 * Shows service statuses, infrastructure health, and incident history.
 * Data comes from the health-service via the backend proxy (port 8000).
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { getHealthClient } from '@/components/admin/adminAuthClients';
import {
  Activity,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Server,
  Database,
  Wifi,
  Clock,
  ChevronDown,
  ChevronUp,
  Cpu,
  HardDrive,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ServiceHealthStatus {
  service: string;
  status: 'healthy' | 'degraded' | 'down' | 'unknown';
  last_check: string;
  response_time_ms?: number;
  details?: {
    diagnostics?: InfrastructureDiagnostics;
  } & Record<string, unknown>;
  site_id?: string;
  version?: string;
  resource_metrics?: {
    memory_mb?: number;
    heap_used_mb?: number;
    event_loop_lag_ms?: number;
    uptime_seconds?: number;
  };
}

interface HealthIncident {
  id: string;
  source_service: string;
  target_service: string;
  error_type: string;
  http_status?: number;
  error_message: string;
  severity: string;
  action_taken: string;
  diagnosis: string;
  suggested_fix?: string;
  ai_diagnosed: boolean;
  repeat_count: number;
  created_at: string;
}

interface SystemHealthStatusResponse {
  source: 'live' | 'database' | 'none';
  agent_status: 'active' | 'inactive' | 'unavailable';
  instanceId?: string;
  role?: 'central' | 'edge';
  services: ServiceHealthStatus[];
  timestamp: string;
}

interface IncidentHistoryResponse {
  incidents: HealthIncident[];
  total: number;
  page: number;
  totalPages: number;
}

interface InfrastructureDiagnostics {
  connection_utilization_pct?: number;
  connections_current?: number;
  connections_available?: number;
  ping_ms?: number;
  operation_latency_ms?: number;
  replication_lag_seconds?: number | null;
  memory_resident_mb?: number;
  memory_virtual_mb?: number;
  wiredtiger_cache_bytes_in_cache?: number;
  wiredtiger_cache_dirty_bytes?: number;
  wiredtiger_cache_max_bytes?: number;
  uptime_seconds?: number;
  slow_operations_count?: number;
  used_memory_mb?: number;
  used_memory_peak_mb?: number;
  maxmemory_mb?: number;
  fragmentation_ratio?: number;
  evicted_keys?: number;
  hit_rate_pct?: number;
  total_keys?: number;
  slow_commands_count?: number;
  connected_clients?: number | null;
  subscription_count?: number | null;
  broker_uptime?: string;
  broker_heap_bytes?: number | null;
  messages_received?: number | null;
  messages_sent?: number | null;
  messages_stored?: number | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 1000) return 'just now';
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

function statusConfig(status: string) {
  switch (status) {
    case 'healthy':
      return {
        color: 'bg-green-500/10 text-green-600 border-green-500/20 dark:text-green-400',
        icon: <CheckCircle2 className="h-4 w-4" />,
        dot: 'bg-green-500',
      };
    case 'degraded':
      return {
        color: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20 dark:text-yellow-400',
        icon: <AlertCircle className="h-4 w-4" />,
        dot: 'bg-yellow-500',
      };
    case 'down':
      return {
        color: 'bg-red-500/10 text-red-600 border-red-500/20 dark:text-red-400',
        icon: <XCircle className="h-4 w-4" />,
        dot: 'bg-red-500',
      };
    default:
      return {
        color: 'bg-gray-500/10 text-gray-600 border-gray-500/20 dark:text-gray-400',
        icon: <AlertCircle className="h-4 w-4" />,
        dot: 'bg-gray-400',
      };
  }
}

function serviceIcon(name: string) {
  switch (name) {
    case 'backend': return <Server className="h-5 w-5" />;
    case 'workflow': return <Cpu className="h-5 w-5" />;
    case 'data-service': return <HardDrive className="h-5 w-5" />;
    case 'agent-service': return <Activity className="h-5 w-5" />;
    case 'knowledge-service': return <Database className="h-5 w-5" />;
    case 'health-service': return <Activity className="h-5 w-5" />;
    case 'mongodb': return <Database className="h-5 w-5" />;
    case 'redis': return <Database className="h-5 w-5" />;
    case 'mqtt-broker':
    case 'mqtt': return <Wifi className="h-5 w-5" />;
    default: return <Server className="h-5 w-5" />;
  }
}

function servicePorts(name: string): string {
  switch (name) {
    case 'backend': return ':8000';
    case 'workflow': return ':8001';
    case 'data-service': return ':8002';
    case 'agent-service': return ':8003';
    case 'knowledge-service': return ':8005';
    case 'health-service': return ':8006';
    default: return '';
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const ServiceCard: React.FC<{ service: ServiceHealthStatus }> = ({ service }) => {
  const cfg = statusConfig(service.status);
  const isInfra = ['mongodb', 'redis', 'mqtt-broker'].includes(service.service);

  return (
    <div className={`border rounded-lg p-4 bg-card ${service.status === 'down' ? 'border-red-500/40' : 'border-border'}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="text-muted-foreground">{serviceIcon(service.service)}</div>
          <div>
            <div className="font-medium text-sm capitalize">
              {service.service.replace(/-/g, ' ')}
              {!isInfra && (
                <span className="text-xs text-muted-foreground ml-1">{servicePorts(service.service)}</span>
              )}
            </div>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${cfg.color}`}>
          {cfg.icon}
          {service.status}
        </span>
      </div>
      <div className="space-y-1 text-xs text-muted-foreground">
        {service.response_time_ms !== undefined && (
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {service.response_time_ms}ms response
          </div>
        )}
        {service.version && service.version !== 'unknown' && (
          <div className="font-mono text-[10px] text-muted-foreground/60">v{service.version}</div>
        )}
        {service.resource_metrics && (
          <div className="flex items-center gap-2 flex-wrap">
            {service.resource_metrics.memory_mb !== undefined && (
              <span>{service.resource_metrics.memory_mb}MB mem</span>
            )}
            {service.resource_metrics.heap_used_mb !== undefined && (
              <span>{service.resource_metrics.heap_used_mb}MB heap</span>
            )}
            {service.resource_metrics.uptime_seconds !== undefined && (
              <span>up {Math.floor(service.resource_metrics.uptime_seconds / 3600)}h</span>
            )}
          </div>
        )}
        {service.last_check && (
          <div>Last check: {relativeTime(service.last_check)}</div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Infrastructure Card — shows deep diagnostics
// ---------------------------------------------------------------------------

function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1073741824).toFixed(2)} GB`;
}

function formatUptime(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

const MetricRow: React.FC<{ label: string; value: string | number | null | undefined; warn?: boolean; critical?: boolean }> = ({ label, value, warn, critical }) => (
  <div className="flex justify-between items-center py-0.5">
    <span className="text-muted-foreground">{label}</span>
    <span className={`font-mono ${critical ? 'text-red-500 font-semibold' : warn ? 'text-yellow-500' : 'text-foreground'}`}>
      {value ?? '—'}
    </span>
  </div>
);

const InfrastructureCard: React.FC<{ service: ServiceHealthStatus }> = ({ service }) => {
  const [expanded, setExpanded] = useState(false);
  const cfg = statusConfig(service.status);
  const diag = service.details?.diagnostics;
  const connectionUtilizationPct = diag?.connection_utilization_pct;

  return (
    <div className={`border rounded-lg bg-card ${service.status === 'down' ? 'border-red-500/40' : 'border-border'}`}>
      {/* Header — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-start justify-between text-left hover:bg-muted/30 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2">
          <div className="text-muted-foreground">{serviceIcon(service.service)}</div>
          <div>
            <div className="font-medium text-sm capitalize">
              {service.service.replace(/-/g, ' ')}
            </div>
            {service.last_check && (
              <div className="text-xs text-muted-foreground mt-0.5">
                Last check: {relativeTime(service.last_check)}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${cfg.color}`}>
            {cfg.icon}
            {service.status}
          </span>
          {diag && (
            expanded
              ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
              : <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Expanded diagnostics */}
      {expanded && diag && (
        <div className="px-4 pb-4 border-t border-border pt-3 text-xs space-y-3">
          {/* MongoDB diagnostics */}
          {service.service === 'mongodb' && (
            <>
              <div>
                <div className="font-semibold text-muted-foreground uppercase tracking-wide mb-1">Connections</div>
                <MetricRow
                  label="Utilization"
                  value={connectionUtilizationPct == null ? '—' : `${connectionUtilizationPct}%`}
                  warn={typeof connectionUtilizationPct === 'number' && connectionUtilizationPct > 70}
                  critical={typeof connectionUtilizationPct === 'number' && connectionUtilizationPct > 90}
                />
                <MetricRow label="Current" value={diag.connections_current} />
                <MetricRow label="Available" value={diag.connections_available} />
              </div>
              <div>
                <div className="font-semibold text-muted-foreground uppercase tracking-wide mb-1">Replication</div>
                {diag.replication_lag_seconds != null ? (
                  <MetricRow
                    label="Lag"
                    value={`${diag.replication_lag_seconds}s`}
                    warn={diag.replication_lag_seconds > 5}
                    critical={diag.replication_lag_seconds > 30}
                  />
                ) : (
                  <div className="text-muted-foreground/60 italic">Standalone (no replica set)</div>
                )}
              </div>
              <div>
                <div className="font-semibold text-muted-foreground uppercase tracking-wide mb-1">Memory</div>
                <MetricRow label="Resident" value={`${diag.memory_resident_mb} MB`} />
                <MetricRow label="Virtual" value={`${diag.memory_virtual_mb} MB`} />
              </div>
              <div>
                <div className="font-semibold text-muted-foreground uppercase tracking-wide mb-1">Cache (WiredTiger)</div>
                <MetricRow label="In Cache" value={formatBytes(diag.wiredtiger_cache_bytes_in_cache)} />
                <MetricRow
                  label="Dirty"
                  value={formatBytes(diag.wiredtiger_cache_dirty_bytes)}
                  warn={typeof diag.wiredtiger_cache_dirty_bytes === 'number' && diag.wiredtiger_cache_dirty_bytes > 0}
                />
                <MetricRow label="Max" value={formatBytes(diag.wiredtiger_cache_max_bytes)} />
              </div>
              <div>
                <div className="font-semibold text-muted-foreground uppercase tracking-wide mb-1">Status</div>
                <MetricRow label="Uptime" value={formatUptime(diag.uptime_seconds)} />
                <MetricRow
                  label="Slow Ops"
                  value={diag.slow_operations_count}
                  warn={typeof diag.slow_operations_count === 'number' && diag.slow_operations_count > 0}
                  critical={typeof diag.slow_operations_count === 'number' && diag.slow_operations_count > 5}
                />
              </div>
            </>
          )}

          {/* Redis diagnostics */}
          {service.service === 'redis' && (
            <>
              <div>
                <div className="font-semibold text-muted-foreground uppercase tracking-wide mb-1">Memory</div>
                <MetricRow label="Used" value={`${diag.used_memory_mb} MB`} />
                <MetricRow label="Peak" value={`${diag.used_memory_peak_mb} MB`} />
                <MetricRow label="Max" value={typeof diag.maxmemory_mb === 'number' ? (diag.maxmemory_mb > 0 ? `${diag.maxmemory_mb} MB` : 'No limit') : '—'} />
                <MetricRow
                  label="Fragmentation"
                  value={`${diag.fragmentation_ratio}x`}
                  warn={typeof diag.fragmentation_ratio === 'number' && typeof diag.used_memory_mb === 'number' && diag.fragmentation_ratio > 1.5 && diag.used_memory_mb > 50}
                  critical={typeof diag.fragmentation_ratio === 'number' && diag.fragmentation_ratio < 1.0}
                />
              </div>
              <div>
                <div className="font-semibold text-muted-foreground uppercase tracking-wide mb-1">Performance</div>
                <MetricRow
                  label="Hit Rate"
                  value={`${diag.hit_rate_pct}%`}
                  warn={typeof diag.hit_rate_pct === 'number' && diag.hit_rate_pct < 50 && diag.hit_rate_pct > 0}
                  critical={typeof diag.hit_rate_pct === 'number' && diag.hit_rate_pct < 20 && diag.hit_rate_pct > 0}
                />
                <MetricRow label="Connected Clients" value={diag.connected_clients} />
                <MetricRow label="Total Keys" value={typeof diag.total_keys === 'number' ? diag.total_keys.toLocaleString() : '—'} />
                <MetricRow
                  label="Slow Commands"
                  value={diag.slow_commands_count}
                  warn={typeof diag.slow_commands_count === 'number' && diag.slow_commands_count > 0}
                />
              </div>
            </>
          )}

          {/* MQTT diagnostics */}
          {(service.service === 'mqtt' || service.service === 'mqtt-broker') && (
            <>
              <div>
                <div className="font-semibold text-muted-foreground uppercase tracking-wide mb-1">Broker</div>
                {diag.connected_clients !== null && (
                  <MetricRow label="Connected Clients" value={diag.connected_clients} />
                )}
                {diag.subscription_count !== null && (
                  <MetricRow label="Subscriptions" value={diag.subscription_count} />
                )}
                {diag.broker_uptime && (
                  <MetricRow label="Uptime" value={diag.broker_uptime} />
                )}
                {diag.broker_heap_bytes != null && (
                  <MetricRow label="Heap" value={formatBytes(diag.broker_heap_bytes)} />
                )}
              </div>
              <div>
                <div className="font-semibold text-muted-foreground uppercase tracking-wide mb-1">Messages</div>
                {diag.messages_received != null && (
                  <MetricRow label="Received" value={diag.messages_received.toLocaleString()} />
                )}
                {diag.messages_sent != null && (
                  <MetricRow label="Sent" value={diag.messages_sent.toLocaleString()} />
                )}
                {diag.messages_stored != null && (
                  <MetricRow
                    label="Stored (queued)"
                    value={diag.messages_stored.toLocaleString()}
                    warn={diag.messages_stored > 100}
                    critical={diag.messages_stored > 1000}
                  />
                )}
              </div>
            </>
          )}

          {/* Fallback if no specific diagnostics match */}
          {!['mongodb', 'redis', 'mqtt', 'mqtt-broker'].includes(service.service) && (
            <pre className="text-[10px] text-muted-foreground/60 overflow-auto max-h-32">
              {JSON.stringify(diag, null, 2)}
            </pre>
          )}
        </div>
      )}

      {/* No diagnostics hint */}
      {expanded && !diag && (
        <div className="px-4 pb-4 border-t border-border pt-3 text-xs text-muted-foreground/60 italic">
          No diagnostics data available. Run a health sweep to collect.
        </div>
      )}
    </div>
  );
};

const IncidentRow: React.FC<{ incident: HealthIncident }> = ({ incident }) => {
  const [expanded, setExpanded] = useState(false);

  const severityColor = {
    critical: 'bg-red-500',
    high: 'bg-red-500',
    medium: 'bg-yellow-500',
    low: 'bg-green-500',
  }[incident.severity] || 'bg-gray-400';

  return (
    <div className="border border-border rounded-lg bg-card">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-muted/50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${severityColor}`} />
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{incident.diagnosis}</div>
            <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
              <span className="capitalize">{incident.target_service}</span>
              <span>{relativeTime(incident.created_at)}</span>
              <span className="text-xs px-1.5 py-0 rounded bg-muted">{incident.error_type}</span>
              <span className="text-xs px-1.5 py-0 rounded bg-muted">{incident.action_taken}</span>
            </div>
          </div>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
      </button>
      {expanded && (
        <div className="px-4 pb-3 border-t border-border pt-3 text-sm text-muted-foreground space-y-1">
          <div><strong>Severity:</strong> {incident.severity}</div>
          <div><strong>Source:</strong> {incident.source_service}</div>
          <div><strong>Target:</strong> {incident.target_service}</div>
          <div><strong>Error:</strong> {incident.error_message}</div>
          {incident.http_status && <div><strong>HTTP Status:</strong> {incident.http_status}</div>}
          <div><strong>Diagnosis:</strong> {incident.diagnosis}</div>
          {incident.suggested_fix && <div><strong>Suggested Fix:</strong> {incident.suggested_fix}</div>}
          <div><strong>Action:</strong> {incident.action_taken}</div>
          <div><strong>Repeat Count:</strong> {incident.repeat_count}</div>
          {incident.ai_diagnosed && <div className="text-xs text-blue-500">AI-diagnosed</div>}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const AdminSystemHealthTab: React.FC = () => {
  const [healthData, setHealthData] = useState<SystemHealthStatusResponse | null>(null);
  const [incidents, setIncidents] = useState<IncidentHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [incidentPage, setIncidentPage] = useState(1);

  const fetchData = useCallback(async (showLoading = true) => {
    if (showLoading) setIsLoading(true);
    setError(null);

    try {
      const client = getHealthClient();
      const [statusRes, incidentsRes] = await Promise.all([
        client.get<SystemHealthStatusResponse>('/status'),
        client.get<IncidentHistoryResponse>('/incidents', { params: { page: incidentPage, limit: 20 } }),
      ]);

      setHealthData(statusRes.data);
      setIncidents(incidentsRes.data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch system health data';
      setError(msg);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [incidentPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchData(false), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchData(false);
  };

  const handleTriggerSweep = async () => {
    try {
      const client = getHealthClient();
      await client.post('/sweep');
      // Refresh after a short delay to let sweep complete
      setTimeout(() => fetchData(false), 3000);
    } catch {
      // Sweep trigger is best-effort
    }
  };

  // Separate services and infrastructure
  const services = healthData?.services?.filter(s =>
    ['backend', 'workflow', 'data-service', 'agent-service', 'knowledge-service', 'health-service'].includes(s.service)
  ) || [];
  const infrastructure = healthData?.services?.filter(s =>
    ['mongodb', 'redis', 'mqtt-broker', 'mqtt'].includes(s.service)
  ) || [];

  const healthyCount = healthData?.services?.filter(s => s.status === 'healthy').length || 0;
  const totalCount = healthData?.services?.length || 0;
  const allHealthy = healthyCount === totalCount && totalCount > 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="h-5 w-5 animate-spin" />
          <span>Loading system health...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400 mb-2">
            <XCircle className="h-5 w-5" />
            <span className="font-medium">Failed to load system health</span>
          </div>
          <p className="text-sm text-red-600/80 dark:text-red-400/80 mb-3">{error}</p>
          <Button variant="outline" size="sm" onClick={() => fetchData()}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold">System Health</h2>
            <p className="text-sm text-muted-foreground">
              {healthData?.agent_status === 'active' ? (
                <span className="flex items-center gap-1 flex-wrap">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                  Health monitor active
                  {healthData.role && (
                    <span className="text-xs px-1.5 py-0 rounded bg-muted font-mono">{healthData.role}</span>
                  )}
                  {healthData.instanceId && (
                    <span className="text-xs text-muted-foreground/60 font-mono">{healthData.instanceId}</span>
                  )}
                  {healthData.source === 'database' && <span className="ml-1 text-yellow-500">(from DB — health service offline)</span>}
                  {healthData.timestamp && <span className="ml-1">| Updated {relativeTime(healthData.timestamp)}</span>}
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />
                  Health monitor inactive
                  {healthData?.source === 'database' && <span className="ml-1">(showing last known data)</span>}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleTriggerSweep}>
              <Activity className="h-4 w-4 mr-1" />
              Run Sweep
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Overall Status Bar */}
        <div className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium ${
          allHealthy
            ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
            : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400'
        }`}>
          {allHealthy ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          {allHealthy ? 'All systems operational' : `${healthyCount}/${totalCount} services healthy`}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
        {/* Services */}
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Services</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {services.map(svc => (
              <ServiceCard key={svc.service} service={svc} />
            ))}
            {services.length === 0 && (
              <div className="col-span-4 text-center py-8 text-muted-foreground text-sm">
                No service data available. Run a health sweep to populate.
              </div>
            )}
          </div>
        </div>

        {/* Infrastructure */}
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Infrastructure
            <span className="ml-2 text-xs font-normal text-muted-foreground/60">Click to expand diagnostics</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {infrastructure.map(svc => (
              <InfrastructureCard key={svc.service} service={svc} />
            ))}
            {infrastructure.length === 0 && (
              <div className="col-span-3 text-center py-8 text-muted-foreground text-sm">
                No infrastructure data available. Run a health sweep to populate.
              </div>
            )}
          </div>
        </div>

        {/* Incident History */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Incident History
              {incidents?.total ? <span className="ml-1 text-xs font-normal">({incidents.total} total)</span> : null}
            </h3>
          </div>

          {incidents && incidents.incidents.length > 0 ? (
            <div className="space-y-2">
              {incidents.incidents.map(incident => (
                <IncidentRow key={incident.id} incident={incident} />
              ))}

              {/* Pagination */}
              {incidents.totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={incidentPage <= 1}
                    onClick={() => setIncidentPage(p => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {incidents.page} of {incidents.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={incidentPage >= incidents.totalPages}
                    onClick={() => setIncidentPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground text-sm border border-border rounded-lg bg-card">
              <Activity className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
              <p>No incidents recorded</p>
              <p className="text-xs mt-1">Incidents will appear here when the SystemHealthAgent detects issues</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
